import { Epic, combineEpics, ofType } from "redux-observable";
import { interval, merge, of } from "rxjs";
import { catchError, filter, map, mergeMap, takeUntil } from "rxjs/operators";
import { selectAuthTokens } from "../../../features/auth/selectors/auth.selector";
import { downloadStartAction } from "../../../features/download/redux/download.actions";
import { IAction } from "../../../interface/action.interface";
import { IErrorDto } from "../../../interface/dto/error.dto";
import { IRootState } from "../../../redux/root.reducer";
import { canAccessOtherUserListBuilder } from "../../../service/user-permission";
import { RunStatus } from "../enum/run-status";
import { selectListBuilderFiltersAndPagination } from "../selectors/filters-and-pagination";
import {
    buildListBuilder,
    checkCreateListBuilderPermission,
    deleteListBuilder,
    duplicateListBuilder,
    fetchListBuilders,
} from "../service/list-builder-table-page.api";

import { listBuilderUsersActions, listBuilderUsersEpic } from ".";
import {
    IListBuilderTableActions,
    ListBuilderTableActionType,
    listBuilderTableCheckCreatePermissionAction,
    listBuilderTableCheckCreatePermissionSuccessAction,
    listBuilderTableErrorAction,
    listBuilderTableLoadAction,
    listBuilderTableStartAutoRefreshAction,
    listBuilderTableStopAutoRefreshAction,
    listBuilderTableSuccessAction,
} from "./list-builder-table-page.action";

const refreshInterval = 10000;

const listBuilderTableEpic: Epic<IAction, IAction, IRootState> = (
    action$,
    state$,
) =>
    merge(
        action$.pipe(
            ofType(ListBuilderTableActionType.LIST_BUILDER_TABLE_INIT),
            mergeMap(() => {
                const role = state$.value.auth?.user?.role;
                return [
                    listBuilderTableLoadAction(),
                    ...(canAccessOtherUserListBuilder(role)
                        ? [listBuilderUsersActions.init()]
                        : []),
                    listBuilderTableCheckCreatePermissionAction(),
                ];
            }),
        ),

        action$.pipe(
            ofType<
                IListBuilderTableActions,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_LOAD
            >(ListBuilderTableActionType.LIST_BUILDER_TABLE_LOAD),
            mergeMap(() =>
                fetchListBuilders(
                    selectListBuilderFiltersAndPagination(state$.value),
                    selectAuthTokens(),
                ).pipe(
                    mergeMap((response) => [
                        listBuilderTableSuccessAction(response),
                        listBuilderTableStartAutoRefreshAction(),
                    ]),
                    catchError((error: IErrorDto) =>
                        of(listBuilderTableErrorAction(error.message)),
                    ),
                    takeUntil(
                        action$.pipe(
                            ofType(
                                ListBuilderTableActionType.LIST_BUILDER_TABLE_LOAD,
                            ),
                        ),
                    ),
                ),
            ),
        ),

        action$.pipe(
            ofType<
                IListBuilderTableActions,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_CHECK_CREATE_PERMISSION
            >(
                ListBuilderTableActionType.LIST_BUILDER_TABLE_CHECK_CREATE_PERMISSION,
            ),
            mergeMap(() =>
                checkCreateListBuilderPermission(selectAuthTokens()).pipe(
                    map((payload) =>
                        listBuilderTableCheckCreatePermissionSuccessAction(
                            payload,
                        ),
                    ),
                    catchError((error: IErrorDto) =>
                        of(listBuilderTableErrorAction(error.message)),
                    ),
                ),
            ),
        ),

        action$.pipe(
            ofType<
                IListBuilderTableActions,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_DUPLICATE
            >(ListBuilderTableActionType.LIST_BUILDER_TABLE_DUPLICATE),
            mergeMap(({ payload }) =>
                duplicateListBuilder(
                    payload.id,
                    selectAuthTokens(),
                    selectListBuilderFiltersAndPagination(state$.value).email,
                ).pipe(
                    mergeMap(() => [
                        listBuilderTableLoadAction(),
                        listBuilderTableCheckCreatePermissionAction(),
                    ]),
                    catchError((error: IErrorDto) =>
                        of(listBuilderTableErrorAction(error.message)),
                    ),
                ),
            ),
        ),

        action$.pipe(
            ofType<
                IListBuilderTableActions,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_DELETE
            >(ListBuilderTableActionType.LIST_BUILDER_TABLE_DELETE),
            mergeMap(({ payload }) =>
                merge(
                    deleteListBuilder(
                        payload.id,
                        selectAuthTokens(),
                        selectListBuilderFiltersAndPagination(state$.value)
                            .email,
                    ).pipe(
                        mergeMap(() => [
                            listBuilderTableLoadAction(),
                            listBuilderTableCheckCreatePermissionAction(),
                        ]),
                        catchError((error: IErrorDto) =>
                            of(listBuilderTableErrorAction(error.message)),
                        ),
                    ),
                ),
            ),
        ),

        action$.pipe(
            ofType<
                IListBuilderTableActions,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_BUILD
            >(ListBuilderTableActionType.LIST_BUILDER_TABLE_BUILD),
            mergeMap(({ payload }) =>
                merge(
                    buildListBuilder(
                        payload.id,
                        selectAuthTokens(),
                        selectListBuilderFiltersAndPagination(state$.value)
                            .email,
                    ).pipe(
                        map(() => listBuilderTableLoadAction()),
                        catchError((error: IErrorDto) =>
                            of(listBuilderTableErrorAction(error.message)),
                        ),
                    ),
                ),
            ),
        ),

        action$.pipe(
            ofType<
                IListBuilderTableActions,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_DOWNLOAD
            >(ListBuilderTableActionType.LIST_BUILDER_TABLE_DOWNLOAD),
            mergeMap(({ payload }) => {
                const email = selectListBuilderFiltersAndPagination(
                    state$.value,
                ).email;
                return [
                    downloadStartAction(
                        `/list-builders/${payload.id}/download${
                            email ? `?email=${encodeURIComponent(email)}` : ""
                        }`,
                    ),
                ];
            }),
        ),

        action$.pipe(
            ofType<
                IListBuilderTableActions,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_START_AUTO_REFRESH
            >(ListBuilderTableActionType.LIST_BUILDER_TABLE_START_AUTO_REFRESH),
            mergeMap(() => {
                const listBuilderState =
                    state$.value.listBuilderTable.listBuilderTable;
                const listBuilders =
                    listBuilderState.listBuilders?.result ?? [];
                const runningListBuilderIds = listBuilders
                    .filter((lb) =>
                        [RunStatus.Pending, RunStatus.Running].includes(
                            lb.lastRunStatus,
                        ),
                    )
                    .map((lb) => lb.listBuilderId);

                return runningListBuilderIds.length > 0
                    ? interval(refreshInterval).pipe(
                          takeUntil(
                              action$.pipe(
                                  filter(
                                      (action) =>
                                          action.type ===
                                              ListBuilderTableActionType.LIST_BUILDER_TABLE_STOP_AUTO_REFRESH ||
                                          action.type ===
                                              ListBuilderTableActionType.LIST_BUILDER_TABLE_LOAD,
                                  ),
                              ),
                          ),
                          mergeMap(() =>
                              fetchListBuilders(
                                  {
                                      id: runningListBuilderIds,
                                      email: selectListBuilderFiltersAndPagination(
                                          state$.value,
                                      ).email,
                                  },
                                  selectAuthTokens(),
                              ).pipe(
                                  map((updatedListBuilders) => {
                                      const updatedBuildersMap = new Map(
                                          updatedListBuilders.result.map(
                                              (lb) => [lb.listBuilderId, lb],
                                          ),
                                      );
                                      const mergedListBuilders =
                                          listBuilders.map(
                                              (lb) =>
                                                  updatedBuildersMap.get(
                                                      lb.listBuilderId,
                                                  ) ?? lb,
                                          );
                                      return listBuilderTableSuccessAction({
                                          total:
                                              listBuilderState.listBuilders
                                                  ?.total ?? 0,
                                          result: mergedListBuilders,
                                      });
                                  }),
                                  catchError((error: IErrorDto) =>
                                      of(
                                          listBuilderTableErrorAction(
                                              error.message,
                                          ),
                                      ),
                                  ),
                              ),
                          ),
                      )
                    : of(listBuilderTableStopAutoRefreshAction());
            }),
        ),

        action$.pipe(
            ofType<
                IListBuilderTableActions,
                | ListBuilderTableActionType.LIST_BUILDER_TABLE_SET_PAGINATION
                | ListBuilderTableActionType.LIST_BUILDER_TABLE_SET_SEARCH_QUERY
                | ListBuilderTableActionType.LIST_BUILDER_TABLE_SET_SORTING
                | ListBuilderTableActionType.LIST_BUILDER_TABLE_DUPLICATE
                | ListBuilderTableActionType.LIST_BUILDER_TABLE_DELETE
                | ListBuilderTableActionType.LIST_BUILDER_TABLE_SET_CURRENT_USER
            >(
                ListBuilderTableActionType.LIST_BUILDER_TABLE_SET_PAGINATION,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_SET_SEARCH_QUERY,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_SET_SORTING,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_DUPLICATE,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_DELETE,
                ListBuilderTableActionType.LIST_BUILDER_TABLE_SET_CURRENT_USER,
            ),
            map(listBuilderTableLoadAction),
        ),
    );

export const listBuilderTablePageEpic = combineEpics<
    IAction,
    IAction,
    IRootState
>(listBuilderTableEpic, listBuilderUsersEpic);
